import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleInitialEmailVerification } from '../utils/emailVerification';
import Header from '../components/Header';

const InitialEmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    const handleVerification = async () => {
      try {
        setVerificationStatus('verifying');
        
        // Get the user ID from the URL parameters
        const userId = searchParams.get('uid');
        
        if (!userId) {
          setVerificationStatus('error');
          setError('Invalid verification link. Missing user ID.');
          return;
        }

        // Handle the email verification
        const result = await handleInitialEmailVerification(userId);
        
        if (result.success) {
          if (result.requiresSignIn) {
            setVerificationStatus('requiresSignIn');
          } else {
            setVerificationStatus('verified');
          }
        } else {
          setVerificationStatus('error');
          setError(result.message);
        }
      } catch (err) {
        setVerificationStatus('error');
        setError('An unexpected error occurred during verification.');
        console.error(err);
      }
    };

    handleVerification();
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/login');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="text-center">
            {verificationStatus === 'verifying' && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
                <h1 className="text-2xl font-bold mb-4">Verifying Your Email</h1>
                <p className="text-gray-300">Please wait while we verify your email address...</p>
              </>
            )}

            {verificationStatus === 'verified' && (
              <>
                <div className="text-6xl mb-4">✅</div>
                <h1 className="text-2xl font-bold mb-4 text-green-400">Email Verified!</h1>
                <p className="text-gray-300 mb-6">
                  Your email address has been successfully verified. Your account is now active and ready to use!
                </p>
                <div className="space-y-3">
                  <button
                    onClick={handleContinue}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Sign In to Your Account
                  </button>
                  <button
                    onClick={handleGoHome}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Go to Homepage
                  </button>
                </div>
                <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                  <h3 className="text-green-400 font-semibold mb-2">Welcome to Trivia Plug!</h3>
                  <ul className="text-gray-300 text-sm space-y-1 text-left">
                    <li>• Your account is now fully activated</li>
                    <li>• You can sign in and start using all features</li>
                    <li>• Create games, join teams, and host trivia events</li>
                    <li>• Your email is verified for future communications</li>
                  </ul>
                </div>
              </>
            )}

            {verificationStatus === 'requiresSignIn' && (
              <>
                <div className="text-6xl mb-4">🔐</div>
                <h1 className="text-2xl font-bold mb-4 text-yellow-400">Sign In Required</h1>
                <p className="text-gray-300 mb-6">{error}</p>
                <div className="space-y-3">
                  <button
                    onClick={handleContinue}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Sign In to Complete Verification
                  </button>
                  <button
                    onClick={handleGoHome}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Return to Homepage
                  </button>
                </div>
                <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                  <h3 className="text-green-400 font-semibold mb-2">Almost there!</h3>
                  <ul className="text-gray-300 text-sm space-y-1 text-left">
                    <li>• Your email verification link was received</li>
                    <li>• Please sign in to complete the verification process</li>
                    <li>• Your account will be fully activated after sign-in</li>
                    <li>• This is a security measure to protect your account</li>
                  </ul>
                </div>
              </>
            )}

            {verificationStatus === 'error' && (
              <>
                <div className="text-6xl mb-4">❌</div>
                <h1 className="text-2xl font-bold mb-4 text-red-400">Verification Failed</h1>
                <p className="text-gray-300 mb-6">{error}</p>
                <div className="space-y-3">
                  <button
                    onClick={handleGoHome}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Return to Homepage
                  </button>
                  <button
                    onClick={handleContinue}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Try Signing In
                  </button>
                </div>
                <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                  <h3 className="text-yellow-400 font-semibold mb-2">Need help?</h3>
                  <ul className="text-gray-300 text-sm space-y-1 text-left">
                    <li>• Make sure you clicked the correct verification link</li>
                    <li>• Check if the link has expired (24 hours)</li>
                    <li>• Try signing in - your email might already be verified</li>
                    <li>• Contact support if you continue to have issues</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InitialEmailVerification; 